// Evidencia automática de un ticket (contrato v1, «evidencia automática al crear
// tickets»): Orión la adjunta en el borde y Django la guarda en
// `ticket.detection_signal.evidencia`. Aquí se LEE de forma defensiva: tickets
// viejos no la traen y los nuevos pueden traerla parcial (recortada a 32 KB,
// canal sin pantalla, etc.). Nada de lo que llega se confía: las URLs se filtran
// a http(s) para que un `javascript:` nunca termine en un href o src.

export type Rol = 'user' | 'assistant';
export type TipoMedio = 'imagen' | 'video' | 'audio' | 'pdf' | 'enlace';

export interface Turno { rol: Rol; texto: string; ts?: string }
export interface Medio { tipo: TipoMedio; url: string; de?: Rol; ts?: string }
export interface Herramienta { tool: string; ok: boolean | null; error?: string; resumen?: string; ts?: string }
export interface PantallaEv {
  ruta?: string;
  titulo?: string;
  modo?: string;
  dispositivo?: string;
  user_agent?: string;
  viewport?: string;
  version_app?: string;
}
export interface Adjunto { document_id: string; nombre?: string; url?: string }
export interface AgenteEv { pasos: string[]; esperado?: string; actual?: string; urls: string[] }

export interface Evidencia {
  version?: number;
  canal?: string;
  session_id?: string;
  thread_id?: string;
  /** Link a la conversación: `conversation_url` si llega; si no, se arma con `thread_id`. */
  conversation_url?: string;
  adjuntos: Adjunto[];
  palabras_del_usuario: string[];
  conversacion: Turno[];
  medios: Medio[];
  herramientas: Herramienta[];
  pantalla?: PantallaEv;
  agente?: AgenteEv;
  pii_enmascarada: boolean;
}

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const str = (v: unknown, max = 4000): string | undefined => {
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t ? t.slice(0, max) : undefined;
};

/** Solo http(s). Cualquier otro esquema (javascript:, data:, file:) se descarta. */
export function urlSegura(v: unknown): string | undefined {
  const s = str(v, 2000);
  if (!s) return undefined;
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.toString() : undefined;
  } catch {
    return undefined;
  }
}

/** Link a la conversación: http(s) absoluto o ruta del propio dashboard («/m/…»). */
function linkConversacion(v: unknown): string | undefined {
  const s = str(v, 2000);
  if (!s) return undefined;
  if (/^\/(?!\/)/.test(s)) return s;
  return urlSegura(s);
}

const TIPOS: TipoMedio[] = ['imagen', 'video', 'audio', 'pdf', 'enlace'];
const rol = (v: unknown): Rol | undefined => (v === 'user' || v === 'assistant' ? v : undefined);

// Si el tipo no llega o no se reconoce, se deduce por la extensión.
function tipoMedio(v: unknown, url: string): TipoMedio {
  const t = str(v)?.toLowerCase();
  if (t && (TIPOS as string[]).includes(t)) return t as TipoMedio;
  if (t === 'image') return 'imagen';
  const path = url.split(/[?#]/)[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp|avif|svg)$/.test(path)) return 'imagen';
  if (/\.(mp4|webm|mov|m4v)$/.test(path)) return 'video';
  if (/\.(mp3|ogg|oga|wav|m4a|aac|opus)$/.test(path)) return 'audio';
  if (/\.pdf$/.test(path)) return 'pdf';
  return 'enlace';
}

function resumenArgs(v: unknown): string | undefined {
  const o = obj(v);
  if (!o) return str(v, 600);
  const r = str(o.resumen, 600);
  if (r) return r;
  try {
    const j = JSON.stringify(o);
    return j && j !== '{}' ? j.slice(0, 600) : undefined;
  } catch {
    return undefined;
  }
}

/** `detection_signal` (o la evidencia directa) → Evidencia normalizada, o null si no hay. */
export function leerEvidencia(signal: unknown): Evidencia | null {
  const raw = obj(obj(signal)?.evidencia);
  if (!raw) return null;

  const palabras = arr(raw.palabras_del_usuario).map((p) => str(p, 1500)).filter((p): p is string => !!p);

  const conversacion: Turno[] = arr(raw.conversacion)
    .map((t): Turno | null => {
      const o = obj(t);
      const texto = str(o?.texto, 1500);
      const r = rol(o?.rol);
      return o && texto && r ? { rol: r, texto, ts: str(o.ts, 40) } : null;
    })
    .filter((t): t is Turno => !!t);

  const vistos = new Set<string>();
  const medios: Medio[] = [];
  for (const m of arr(raw.medios)) {
    const o = obj(m);
    const url = urlSegura(o?.url);
    if (!o || !url || vistos.has(url)) continue;
    vistos.add(url);
    medios.push({ tipo: tipoMedio(o.tipo, url), url, de: rol(o.de), ts: str(o.ts, 40) });
  }

  const herramientas: Herramienta[] = arr(raw.herramientas)
    .map((h): Herramienta | null => {
      const o = obj(h);
      const tool = str(o?.tool, 120);
      if (!o || !tool) return null;
      const error = str(o.error, 1200);
      const ok = typeof o.ok === 'boolean' ? o.ok : error ? false : null;
      return { tool, ok, error, resumen: resumenArgs(o.args), ts: str(o.ts, 40) };
    })
    .filter((h): h is Herramienta => !!h);

  const p = obj(raw.pantalla);
  const pantalla: PantallaEv | undefined = p
    ? {
        ruta: str(p.ruta, 500),
        titulo: str(p.titulo, 200),
        modo: str(p.modo, 20),
        dispositivo: str(p.dispositivo, 20),
        user_agent: str(p.user_agent, 400),
        viewport: str(p.viewport, 20),
        version_app: str(p.version_app, 80),
      }
    : undefined;
  const hayPantalla = pantalla && Object.values(pantalla).some(Boolean);

  const a = obj(raw.agente);
  const agente: AgenteEv | undefined = a
    ? {
        pasos: arr(a.pasos).map((x) => str(x, 600)).filter((x): x is string => !!x),
        esperado: str(a.esperado, 1500),
        actual: str(a.actual, 1500),
        urls: Array.from(new Set(arr(a.urls ?? a.evidencia_urls).map(urlSegura).filter((u): u is string => !!u))),
      }
    : undefined;
  const hayAgente = agente && (agente.pasos.length || agente.esperado || agente.actual || agente.urls.length);

  // Adjuntos del usuario (documentos subidos al chat): `document_id` y, si viene, nombre/url.
  const adjuntos: Adjunto[] = [];
  for (const x of arr(raw.adjuntos)) {
    const o = obj(x);
    const id = o ? str(o.document_id ?? o.id, 120) : str(x, 120);
    if (!id || adjuntos.some((a) => a.document_id === id)) continue;
    adjuntos.push({ document_id: id, nombre: o ? str(o.nombre ?? o.filename ?? o.name, 200) : undefined, url: o ? urlSegura(o.url) : undefined });
  }

  const threadId = str(raw.thread_id, 120);
  // Orión manda conversation_url en null: el link se arma con el thread_id hacia la
  // pantalla del dashboard que abre un hilo (Asistente del shell: ?hilo=<id>).
  const conversationUrl =
    linkConversacion(raw.conversation_url) ??
    (threadId && /^[\w-]+$/.test(threadId) ? `/m/orion/asistente?hilo=${encodeURIComponent(threadId)}` : undefined);

  return {
    version: typeof raw.version === 'number' ? raw.version : undefined,
    canal: str(raw.canal, 40),
    session_id: str(raw.session_id, 120),
    thread_id: threadId,
    conversation_url: conversationUrl,
    adjuntos,
    palabras_del_usuario: palabras,
    conversacion,
    medios,
    herramientas,
    pantalla: hayPantalla ? pantalla : undefined,
    agente: hayAgente ? agente : undefined,
    pii_enmascarada: raw.pii_enmascarada === true,
  };
}

/** ¿Trae algo que mostrar? (una evidencia `{}` cuenta como ausente). */
export function tieneContenido(e: Evidencia | null): e is Evidencia {
  return !!e && !!(
    e.palabras_del_usuario.length ||
    e.conversacion.length ||
    e.medios.length ||
    e.adjuntos.length ||
    e.herramientas.length ||
    e.pantalla ||
    e.agente ||
    e.conversation_url
  );
}

export const CANAL_LABEL: Record<string, string> = {
  dashboard: 'Panel (dashboard)',
  whatsapp: 'WhatsApp',
  messenger: 'Messenger',
  instagram: 'Instagram',
  voz: 'Voz',
  web_publico: 'Chat del sitio público',
  automatizacion: 'Automatización',
  otro: 'Otro canal',
};

export const DISPOSITIVO_LABEL: Record<string, string> = { ios: 'iPhone / iPad', android: 'Android', desktop: 'Computadora' };
export const MODO_LABEL: Record<string, string> = { pwa: 'app instalada (PWA)', navegador: 'navegador' };
